import { useEffect, useState } from 'react'
import { ExternalLink, FileSpreadsheet, FileText, Loader2 } from 'lucide-react'
import {
  ingestionService,
  type EmailEnvelope,
  type SourceAnchor,
  type SourceFileRef,
  type SourcePreview,
} from '@/services/ingestion'
import { formatDeskDateTime } from '@/utils/dates'
import { HighlightedText } from './SourceHighlight'

type Props = {
  ingestionId: string
  files: SourceFileRef[]
  activeSha?: string | null
  anchor?: SourceAnchor | null
  envelope?: EmailEnvelope | null
  onOpenFull?: (sha: string) => void
  compact?: boolean
}

export default function SourcePreviewPane({
  ingestionId,
  files,
  activeSha,
  anchor,
  envelope,
  onOpenFull,
  compact = false,
}: Props) {
  const emailFile = files.find((f) => f.kind === 'email_source')
  const attachments = files.filter((f) => f.kind !== 'email_source')
  const [tab, setTab] = useState<'email' | 'files'>(emailFile ? 'email' : 'files')
  const [sha, setSha] = useState(activeSha ?? emailFile?.sha256 ?? files[0]?.sha256 ?? null)
  const [preview, setPreview] = useState<SourcePreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (activeSha) {
      setSha(activeSha)
      const f = files.find((x) => x.sha256 === activeSha)
      setTab(f?.kind === 'email_source' || !f ? 'email' : 'files')
    }
  }, [activeSha, files])

  useEffect(() => {
    if (!sha || !ingestionId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    void ingestionService
      .getSourcePreview(ingestionId, sha)
      .then((pv) => {
        if (!cancelled) setPreview(pv)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        setError(typeof detail === 'string' ? detail : 'Could not load this source.')
        setPreview(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [ingestionId, sha])

  const env = preview?.envelope ?? envelope
  const body = preview?.text ?? ''
  // An evidence anchor belongs to the file selected for the datapoint. If the
  // reviewer manually changes tabs, do not highlight the same coordinate in a
  // different document.
  const activeAnchor = anchor && (!activeSha || sha === activeSha) ? anchor : null
  const bodyWindow = textWindow(body, activeAnchor, compact ? 900 : 4000)
  const previewWindow = textWindow(preview?.text ?? '', activeAnchor, compact ? 900 : 4000)
  const workbookCell = preview?.kind === 'xlsx'
    ? resolveWorkbookCell(preview, activeAnchor)
    : null

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-stone-800 bg-black/30">
      <div className="flex items-center gap-1 border-b border-stone-800 px-2 py-1.5">
        {emailFile && (
          <button
            type="button"
            onClick={() => {
              setTab('email')
              setSha(emailFile.sha256)
            }}
            className={`rounded px-2 py-0.5 text-[10px] ${
              tab === 'email' ? 'bg-amber-400/15 text-amber-200' : 'text-stone-500 hover:text-stone-300'
            }`}
          >
            Email Body
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setTab('files')
            setSha(attachments[0]?.sha256 ?? sha)
          }}
          className={`rounded px-2 py-0.5 text-[10px] ${
            tab === 'files' ? 'bg-amber-400/15 text-amber-200' : 'text-stone-500 hover:text-stone-300'
          }`}
        >
          Attachments ({attachments.length})
        </button>
      </div>

      {tab === 'files' && attachments.length > 0 && (
        <div className="flex gap-1 overflow-x-auto border-b border-stone-800 px-2 py-1">
          {attachments.map((f) => (
            <button
              key={f.sha256}
              type="button"
              onClick={() => setSha(f.sha256)}
              className={`flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] ${
                sha === f.sha256 ? 'bg-white/[0.06] text-stone-200' : 'text-stone-500 hover:text-stone-300'
              }`}
            >
              {/\.(xlsx|xlsm|csv)$/i.test(f.filename) ? (
                <FileSpreadsheet size={9} className="text-emerald-400" />
              ) : (
                <FileText size={9} className="text-stone-400" />
              )}
              <span className="max-w-[140px] truncate">{f.filename}</span>
            </button>
          ))}
        </div>
      )}

      <div className={`min-h-0 flex-1 overflow-y-auto px-3 py-2 ${compact ? 'max-h-[280px]' : ''}`}>
        {loading && (
          <p className="flex items-center justify-center gap-2 py-8 text-[11px] text-stone-500">
            <Loader2 size={12} className="animate-spin" /> Loading source…
          </p>
        )}
        {error && !loading && <p className="py-6 text-center text-[11px] text-red-300">{error}</p>}
        {!loading && !error && tab === 'email' && (
          <div className="space-y-2 text-[11px]">
            <div className="space-y-0.5 rounded border border-stone-800 bg-black/40 px-2 py-1.5">
              <Row label="From" value={env?.fromName || env?.fromEmail || '—'} />
              <Row label="To" value={(env?.to ?? []).join(', ') || '—'} />
              <Row
                label="Date"
                value={formatDeskDateTime(env?.sentAt) || '—'}
              />
              <Row label="Subject" value={env?.subject || '—'} />
            </div>
            {body.trim() ? (
              <div className="leading-relaxed text-stone-300">
                <HighlightedText text={bodyWindow.text} anchor={bodyWindow.anchor} />
                {bodyWindow.truncated && (
                  <p className="mt-1 text-[9px] text-stone-500">Showing the evidence context from the email.</p>
                )}
              </div>
            ) : (
              <p className="text-stone-500">(no readable body)</p>
            )}
          </div>
        )}
        {!loading && !error && tab === 'files' && preview && preview.kind !== 'email' && (
          <div className="space-y-2 text-[11px] text-stone-300">
            <div className="flex items-center gap-1.5 text-stone-400">
              {preview.kind === 'xlsx' ? <FileSpreadsheet size={11} /> : <FileText size={11} />}
              <span className="truncate">{preview.filename}</span>
            </div>
            {preview.text ? (
              <div className="font-sans text-[11px] leading-relaxed">
                <HighlightedText
                  text={previewWindow.text}
                  anchor={previewWindow.anchor}
                  pageStartLines={preview.kind === 'pdf' && !previewWindow.truncated ? preview.pageStartLines : undefined}
                  mono={preview.kind !== 'pdf'}
                />
                {previewWindow.truncated && (
                  <p className="mt-1 text-[9px] text-stone-500">Showing the evidence context from this source.</p>
                )}
              </div>
            ) : preview.kind === 'xlsx' && workbookCell ? (
              <div className="rounded border border-amber-400/30 bg-amber-400/10 px-2 py-2">
                <div className="text-[9px] uppercase tracking-wide text-amber-300/80">Evidence cell</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="font-mono text-[11px] text-amber-200">{workbookCell.reference}</span>
                  <span className="font-mono text-[12px] text-stone-100">
                    {formatCellValue(workbookCell.value)}
                  </span>
                </div>
              </div>
            ) : preview.kind === 'xlsx' && preview.sheets?.[0] ? (
              <p className="text-stone-500">
                Workbook · {preview.sheets.length} sheet{preview.sheets.length === 1 ? '' : 's'} · open the full source to inspect its cells.
              </p>
            ) : preview.kind === 'image' ? (
              <p className="text-stone-500">
                Image evidence has no exact renderable coordinate in this compact pane. Open the full source to inspect it.
              </p>
            ) : (
              <p className="text-stone-500">{preview.detail || 'Open the full source to review this file.'}</p>
            )}
          </div>
        )}
        {!loading && !error && tab === 'files' && attachments.length === 0 && (
          <p className="py-6 text-center text-[11px] text-stone-500">No attachments on this package.</p>
        )}
      </div>

      {sha && onOpenFull && (
        <div className="border-t border-stone-800 px-3 py-2">
          <button
            type="button"
            onClick={() => onOpenFull(sha)}
            className="inline-flex items-center gap-1 text-[10px] text-amber-300 hover:text-amber-200"
          >
            <ExternalLink size={10} /> Open full {tab === 'email' ? 'email' : 'source'}
          </button>
        </div>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <span className="w-[52px] shrink-0 text-stone-500">{label}</span>
      <span className="min-w-0 truncate text-stone-200">{value}</span>
    </div>
  )
}

function textWindow(text: string, anchor: SourceAnchor | null, limit: number): {
  text: string
  anchor: SourceAnchor | null
  truncated: boolean
} {
  if (!text) return { text: '', anchor, truncated: false }
  const lines = text.split('\n')
  const anchoredLine = findAnchorLine(lines, anchor)
  if (anchor && anchoredLine !== null) {
    const radius = 8
    const start = Math.max(0, anchoredLine - radius)
    const end = Math.min(lines.length, anchoredLine + radius + 1)
    return {
      text: lines.slice(start, end).join('\n'),
      anchor: { ...anchor, lineNo: anchoredLine - start + 1 },
      truncated: start > 0 || end < lines.length,
    }
  }
  return {
    text: text.slice(0, limit),
    anchor,
    truncated: text.length > limit,
  }
}

function findAnchorLine(lines: string[], anchor: SourceAnchor | null): number | null {
  if (!anchor) return null
  const probe = (anchor.snippet ?? '').trim().slice(0, 60)
  if (probe.length >= 4) {
    const index = lines.findIndex((line) => line.includes(probe))
    if (index >= 0) return index
    const shorter = probe.slice(0, 24)
    if (shorter.length >= 4) {
      const shorterIndex = lines.findIndex((line) => line.includes(shorter))
      if (shorterIndex >= 0) return shorterIndex
    }
  }
  if (anchor.lineNo && anchor.lineNo >= 1 && anchor.lineNo <= lines.length) {
    return anchor.lineNo - 1
  }
  return null
}

function resolveWorkbookCell(preview: SourcePreview, anchor: SourceAnchor | null): {
  reference: string
  value: string | number | boolean | null
} | null {
  const match = /^(?:(.+)!)?([A-Z]+)(\d+)$/.exec(anchor?.cell ?? '')
  if (!match) return null
  const sheet = match[1]
    ? preview.sheets?.find((candidate) => candidate.name === match[1])
    : preview.sheets?.[0]
  if (!sheet) return null
  const column = match[2].split('').reduce((value, char) => value * 26 + char.charCodeAt(0) - 64, 0) - 1
  const row = Number(match[3]) - 1
  if (row < 0 || column < 0 || row >= sheet.rows.length || column >= (sheet.rows[row]?.length ?? 0)) {
    return null
  }
  return {
    reference: `${sheet.name}!${match[2]}${match[3]}`,
    value: sheet.rows[row][column],
  }
}

function formatCellValue(value: string | number | boolean | null): string {
  if (typeof value === 'number') return value.toLocaleString()
  if (value === null || value === '') return '—'
  return String(value)
}
